import { useEffect, useMemo, useState } from "react";
import {
  CalendarPlus,
  Clock,
  MapPin,
  Users,
  Trash,
  PencilSimple,
  X,
  Eye,
  EyeSlash,
  Stop,
  Play,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  listEvents,
  createEvent,
  updateEvent,
  cancelEvent,
  uncancelEvent,
  deleteEvent,
  listRsvpsForEvent,
  getRsvpCounts,
  formatEventWhen,
} from "@/lib/events";

/**
 * Admin · /admin/events
 *
 * List + create/edit/cancel/delete events. Click a row to see full RSVP
 * list grouped by status.
 */
export default function AdminEvents() {
  const { profile } = useAuth();
  const [events, setEvents] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [countsById, setCountsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | { ...event } or {} for new
  const [selectedId, setSelectedId] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      // Admin needs to see cancelled + upcoming + recent past.
      const { data, error } = await supabase
        .from("events")
        .select(
          "id, organization_id, classroom_id, title, description, location, starts_at, ends_at, visibility, rsvp_enabled, rsvp_deadline, max_capacity, cover_image_url, cancelled_at, created_by, created_at, updated_at",
        )
        .order("starts_at", { ascending: true });
      if (error) throw error;
      setEvents(data || []);

      // RSVP count per event (one RPC call per event; acceptable for preview).
      const counts = {};
      for (const ev of data || []) {
        // eslint-disable-next-line no-await-in-loop
        counts[ev.id] = await getRsvpCounts(ev.id).catch(() => ({
          going: 0, goingAttendees: 0, maybe: 0, notGoing: 0,
        }));
      }
      setCountsById(counts);
    } catch (err) {
      toast.error(err.message || "Could not load events");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!profile?.organization_id) return;
    (async () => {
      const { data, error } = await supabase
        .from("classrooms")
        .select("id, name")
        .eq("organization_id", profile.organization_id)
        .order("name");
      if (!error) setClassrooms(data || []);
    })();
    refresh();
  }, [profile?.organization_id]);

  const kpis = useMemo(() => {
    const now = Date.now();
    return {
      upcoming: events.filter(
        (e) => !e.cancelled_at && new Date(e.starts_at).getTime() >= now,
      ).length,
      rsvpsOpen: events.filter(
        (e) =>
          !e.cancelled_at &&
          e.rsvp_enabled &&
          (!e.rsvp_deadline || new Date(e.rsvp_deadline).getTime() >= now) &&
          new Date(e.starts_at).getTime() >= now,
      ).length,
      cancelled: events.filter((e) => e.cancelled_at).length,
    };
  }, [events]);

  return (
    <div data-testid="admin-events" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Calendar
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900">
            Events & RSVPs
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Plan gatherings, open RSVPs, and track responses from families.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-events-new"
          onClick={() =>
            setEditor({
              title: "",
              description: "",
              location: "",
              starts_at: "",
              ends_at: "",
              visibility: "org",
              classroom_id: null,
              rsvp_enabled: true,
              rsvp_deadline: "",
              max_capacity: "",
            })
          }
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover"
        >
          <CalendarPlus size={16} weight="bold" />
          New event
        </button>
      </header>

      <section
        data-testid="admin-events-kpis"
        className="grid gap-3 sm:grid-cols-3"
      >
        <Kpi testid="admin-events-kpi-upcoming" label="Upcoming" value={kpis.upcoming} />
        <Kpi testid="admin-events-kpi-rsvps-open" label="RSVPs open" value={kpis.rsvpsOpen} />
        <Kpi testid="admin-events-kpi-cancelled" label="Cancelled" value={kpis.cancelled} />
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <p
          data-testid="admin-events-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-8 text-center text-sm text-neutral-500"
        >
          No events yet. Click "New event" to create your first one.
        </p>
      ) : (
        <ul data-testid="admin-events-list" className="space-y-2">
          {events.map((ev) => {
            const counts = countsById[ev.id] || { going: 0, goingAttendees: 0, maybe: 0, notGoing: 0 };
            const isCancelled = !!ev.cancelled_at;
            return (
              <li key={ev.id}>
                <div
                  data-testid={`admin-events-row-${ev.id}`}
                  data-cancelled={isCancelled}
                  className={`flex flex-wrap items-start gap-4 rounded-2xl border bg-white p-4 ${
                    isCancelled ? "border-neutral-200 opacity-60" : "border-neutral-200"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-heading text-base font-semibold text-neutral-900">
                        {ev.title}
                      </h3>
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                        {ev.visibility === "org" ? "School-wide" : "Classroom"}
                      </span>
                      {isCancelled && (
                        <span className="rounded-full bg-[#C6851E]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#C6851E]">
                          Cancelled
                        </span>
                      )}
                    </div>
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-600">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={14} weight="duotone" className="text-primary-hover" />
                        {formatEventWhen(ev.starts_at, ev.ends_at)}
                      </span>
                      {ev.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={14} weight="duotone" className="text-primary-hover" />
                          {ev.location}
                        </span>
                      )}
                    </p>
                    <p
                      data-testid={`admin-events-row-${ev.id}-counts`}
                      className="mt-1 text-xs text-neutral-500"
                    >
                      <span className="font-medium text-primary-hover">
                        {counts.going} going
                      </span>
                      {" · "}
                      {counts.goingAttendees} attendees
                      {" · "}
                      {counts.maybe} maybe
                      {" · "}
                      {counts.notGoing} declined
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      data-testid={`admin-events-row-${ev.id}-view`}
                      onClick={() => setSelectedId(ev.id)}
                      title="View RSVPs"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    >
                      <Users size={14} weight="bold" />
                    </button>
                    <button
                      type="button"
                      data-testid={`admin-events-row-${ev.id}-edit`}
                      onClick={() => setEditor(ev)}
                      title="Edit"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    {isCancelled ? (
                      <button
                        type="button"
                        data-testid={`admin-events-row-${ev.id}-uncancel`}
                        onClick={async () => {
                          try {
                            await uncancelEvent(ev.id);
                            toast.success("Reinstated");
                            refresh();
                          } catch (err) {
                            toast.error(err.message || "Could not reinstate");
                          }
                        }}
                        title="Reinstate"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary-hover hover:bg-primary/20"
                      >
                        <Play size={14} weight="bold" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid={`admin-events-row-${ev.id}-cancel`}
                        onClick={async () => {
                          if (!window.confirm("Cancel this event? Parents will see it as cancelled.")) return;
                          try {
                            await cancelEvent(ev.id);
                            toast.success("Cancelled");
                            refresh();
                          } catch (err) {
                            toast.error(err.message || "Could not cancel");
                          }
                        }}
                        title="Cancel"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                      >
                        <Stop size={14} weight="bold" />
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`admin-events-row-${ev.id}-delete`}
                      onClick={async () => {
                        if (!window.confirm("Delete this event permanently? This cannot be undone.")) return;
                        try {
                          await deleteEvent(ev.id);
                          toast.success("Deleted");
                          refresh();
                        } catch (err) {
                          toast.error(err.message || "Could not delete");
                        }
                      }}
                      title="Delete"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 hover:bg-[#C6851E]/10 hover:text-[#C6851E]"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editor && (
        <EventEditor
          event={editor}
          classrooms={classrooms}
          organizationId={profile?.organization_id}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            refresh();
          }}
        />
      )}

      {selectedId && (
        <RsvpListDrawer
          eventId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function Kpi({ testid, label, value }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-neutral-200 bg-white p-4">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function EventEditor({ event, classrooms, organizationId, onClose, onSaved }) {
  const isNew = !event.id;
  const [form, setForm] = useState({
    title: event.title || "",
    description: event.description || "",
    location: event.location || "",
    starts_at: toLocalInput(event.starts_at),
    ends_at: toLocalInput(event.ends_at),
    visibility: event.visibility || "org",
    classroom_id: event.classroom_id || "",
    rsvp_enabled: event.rsvp_enabled !== false,
    rsvp_deadline: toLocalInput(event.rsvp_deadline),
    max_capacity: event.max_capacity ?? "",
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!form.title.trim() || !form.starts_at) {
      toast.error("Title and start time are required.");
      return;
    }
    const payload = {
      title: form.title.trim(),
      description: form.description || null,
      location: form.location || null,
      starts_at: new Date(form.starts_at).toISOString(),
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      visibility: form.visibility,
      classroom_id: form.visibility === "classroom" ? form.classroom_id || null : null,
      rsvp_enabled: form.rsvp_enabled,
      rsvp_deadline: form.rsvp_deadline ? new Date(form.rsvp_deadline).toISOString() : null,
      max_capacity:
        form.max_capacity === "" || form.max_capacity === null
          ? null
          : Number(form.max_capacity),
    };
    if (form.visibility === "classroom" && !payload.classroom_id) {
      toast.error("Pick a classroom or switch to school-wide.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await createEvent({ ...payload, organization_id: organizationId });
        toast.success("Event created");
      } else {
        await updateEvent(event.id, payload);
        toast.success("Event saved");
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="admin-events-editor"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">
            {isNew ? "New event" : "Edit event"}
          </h2>
          <button
            type="button"
            data-testid="admin-events-editor-close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="space-y-3">
          <Field label="Title" required>
            <input
              data-testid="admin-events-editor-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Spring Family Picnic"
            />
          </Field>
          <Field label="Description">
            <textarea
              data-testid="admin-events-editor-description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts at" required>
              <input
                data-testid="admin-events-editor-starts-at"
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </Field>
            <Field label="Ends at">
              <input
                data-testid="admin-events-editor-ends-at"
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </Field>
          </div>
          <Field label="Location">
            <input
              data-testid="admin-events-editor-location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Riverfront Park, Meadow A"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Visibility">
              <select
                data-testid="admin-events-editor-visibility"
                value={form.visibility}
                onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
              >
                <option value="org">School-wide</option>
                <option value="classroom">Classroom</option>
              </select>
            </Field>
            {form.visibility === "classroom" && (
              <Field label="Classroom">
                <select
                  data-testid="admin-events-editor-classroom"
                  value={form.classroom_id || ""}
                  onChange={(e) => setForm({ ...form, classroom_id: e.target.value })}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
                >
                  <option value="">Select…</option>
                  {classrooms.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="RSVP deadline">
              <input
                data-testid="admin-events-editor-rsvp-deadline"
                type="datetime-local"
                value={form.rsvp_deadline}
                onChange={(e) => setForm({ ...form, rsvp_deadline: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </Field>
            <Field label="Max capacity (optional)">
              <input
                data-testid="admin-events-editor-max-capacity"
                type="number"
                min={1}
                value={form.max_capacity}
                onChange={(e) => setForm({ ...form, max_capacity: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
                placeholder="e.g. 80"
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              data-testid="admin-events-editor-rsvp-enabled"
              checked={form.rsvp_enabled}
              onChange={(e) => setForm({ ...form, rsvp_enabled: e.target.checked })}
            />
            RSVPs enabled
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="admin-events-editor-save"
            disabled={saving}
            onClick={handleSave}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RsvpListDrawer({ eventId, onClose }) {
  const [rsvps, setRsvps] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const rows = await listRsvpsForEvent(eventId);
        setRsvps(rows);
      } catch (err) {
        toast.error(err.message || "Could not load RSVPs");
        setRsvps([]);
      }
    })();
  }, [eventId]);

  return (
    <div
      data-testid="admin-events-rsvp-drawer"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div className="max-h-[80vh] w-full max-w-xl overflow-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">
            RSVP responses
          </h2>
          <button
            type="button"
            data-testid="admin-events-rsvp-drawer-close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        {rsvps === null ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-7 w-7 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          </div>
        ) : rsvps.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
            No responses yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {rsvps.map((r) => (
              <li
                key={r.id}
                data-testid={`admin-events-rsvp-row-${r.id}`}
                className="flex items-center justify-between rounded-2xl border border-neutral-200 p-3"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-900">
                    {r.families?.display_name || "—"}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {r.profiles?.full_name || r.profiles?.email}
                    {r.attendee_count > 1 && ` · ${r.attendee_count} attending`}
                  </p>
                  {r.note && <p className="mt-0.5 text-xs text-neutral-600">"{r.note}"</p>}
                </div>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    r.status === "going"
                      ? "border-primary/40 bg-primary/10 text-primary-hover"
                      : r.status === "maybe"
                        ? "border-secondary/50 bg-secondary/20 text-neutral-900"
                        : "border-neutral-200 bg-neutral-100 text-neutral-600"
                  }`}
                >
                  {r.status.replace("_", " ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
        {label}
        {required && <span className="text-[#C6851E]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}
